import type { DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridToolbar, type GridCellProps, type GridDataStateChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useCreateUserMutation, useDeleteUserMutation, useLazyListUsersQuery } from "../../api/admin/adminUserApi";
import AppGrid from "../../components/grid/appGrid";
import { AppRoles } from "../../constants/appRoles";
import type { CustomerDto } from "../../contracts";

const roles: string[] = Object.values(AppRoles);
const customerModes = ["Existing customer", "New customer"];
const initialSort: SortDescriptor[] = [{ field: "userName", dir: "asc" }];

const RolesDataCell = (props: GridCellProps) => {
    const roles = Array.isArray(props.dataItem.roles) ? props.dataItem.roles : [];
    return <td className="k-table-td">{roles.length > 0 ? roles.join(", ") : "-"}</td>;
};

const UserManagement: React.FC = () => {
    const [triggerListUsersQuery, { data, isFetching }] = useLazyListUsersQuery();
    const { data: customersData, isFetching: isFetchingCustomers, refetch: refetchCustomers } = useListCustomersQuery({ take: 1000 });
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<string[]>(["User"]);
    const [customerMode, setCustomerMode] = useState(customerModes[0]);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [customerName, setCustomerName] = useState("");
    const [dataState, setDataState] = useState<DataSourceRequestState>({
        skip: 0,
        take: 20,
        sort: initialSort
    });

    useEffect(() => {
        triggerListUsersQuery({
            ...dataState
        });
    }, [triggerListUsersQuery, dataState]);

    const effectiveSelectedCustomer = selectedCustomer ?? customersData?.data[0] ?? null;

    const refreshUsers = () => {
        triggerListUsersQuery({
            ...dataState
        });
    };

    const pageChange = (event: GridPageChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            skip: event.page.skip,
            take: event.page.take
        }));
    };

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        setDataState({
            ...event.dataState,
            skip: event.dataState.skip,
            take: event.dataState.take
        });
    };

    const sortChange = (event: GridSortChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            sort: event.sort
        }));
    };

    const resetCreateForm = () => {
        setEmail("");
        setPhoneNumber("");
        setPassword("");
        setConfirmPassword("");
        setSelectedRoles(["User"]);
        setCustomerMode(customerModes[0]);
        setSelectedCustomer(customersData?.data[0] ?? null);
        setCustomerName("");
    };

    const handleCreate = async () => {
        try {
            await createUser({
                email,
                phone_number: phoneNumber,
                password,
                confirm_password: confirmPassword,
                roles: selectedRoles,
                customer_id: customerMode === customerModes[0] ? effectiveSelectedCustomer?.id ?? null : null,
                customer_name: customerMode === customerModes[1] ? customerName : null
            }).unwrap();

            setErrorMessage(null);
            setIsCreateDialogOpen(false);
            resetCreateForm();
            refetchCustomers();
            refreshUsers();
        } catch {
            setErrorMessage("Unable to create user.");
        }
    };

    const handleDelete = async (userId: string, userName: string) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete ${userName}?`);
        if (!isConfirmed) {
            return;
        }

        try {
            const response = await deleteUser({ user_id: userId }).unwrap();
            if (response.is_success) {
                setErrorMessage(null);
                refreshUsers();
                return;
            }

            setErrorMessage(response.error_message ?? "Unable to delete user.");
        } catch {
            setErrorMessage("Unable to delete user.");
        }
    };

    const DeleteActionCell = (props: GridCellProps) => (
        <td className="k-table-td text-center align-middle">
            <Button
                type="button"
                themeColor="error"
                fillMode="outline"
                size="small"
                disabled={isDeleting || isFetching}
                onClick={() => handleDelete(
                    props.dataItem.id as string,
                    props.dataItem.user_name as string
                )}
            >
                Delete
            </Button>
        </td>
    );

    const customerInput = customerMode === customerModes[0]
        ? (
                <DropDownList
                    label="Customer"
                    data={customersData?.data ?? []}
                    textField="name"
                    dataItemKey="id"
                    value={effectiveSelectedCustomer}
                    loading={isFetchingCustomers}
                    onChange={event => setSelectedCustomer(event.value)}
                />
            )
        : (
                <Input label="New Customer Name" value={customerName} onChange={event => setCustomerName(String(event.value ?? ""))} />
            );

    const canCreate = email.trim() !== "" && password !== "" && confirmPassword !== "" && selectedRoles.length > 0
        && (
            customerMode === customerModes[0]
                ? effectiveSelectedCustomer != null
                : customerName.trim() !== ""
        );

    return (
        <div className="container-fluid py-3">
            <div className="d-flex flex-column gap-2 mb-3">
                <h2 className="h4 mb-0">User Management</h2>
                <p className="text-muted mb-0">Application users from identity store.</p>
            </div>

            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}

            <AppGrid
                showLoader={isFetching || isDeleting || isCreating}
                data={data?.data ?? []}
                total={data?.total ?? 0}
                pageable={{ buttonCount: 3, pageSizes: [20, 50, 100] }}
                sortable
                initialState={dataState}
                skip={dataState.skip}
                take={dataState.take}
                sort={dataState.sort}
                onPageChange={pageChange}
                onDataStateChange={dataStateChange}
                onSortChange={sortChange}
                style={{
                    width: "100%"
                }}
            >
                <GridToolbar>
                    <Button type="button" themeColor="primary" onClick={() => setIsCreateDialogOpen(true)}>
                        Create User
                    </Button>
                    <Button type="button" fillMode="outline" onClick={refreshUsers}>
                        Refresh
                    </Button>
                </GridToolbar>
                <GridColumn field="email" title="Email" width="320px" />
                <GridColumn field="customer_name" title="Customer" width="220px" sortable={false} />
                <GridColumn field="phone_number" title="Phone" width="180px" />
                <GridColumn field="roles" title="Roles" width="220px" cells={{ data: RolesDataCell }} sortable={false} />
                <GridColumn field="email_confirmed" title="Email Confirmed" width="150px" />
                <GridColumn field="phone_number_confirmed" title="Phone Confirmed" width="150px" />
                <GridColumn field="two_factor_enabled" title="2FA" width="110px" />
                <GridColumn field="lockout_enabled" title="Lockout" width="110px" />
                <GridColumn field="access_failed_count" title="Access Failed" width="130px" />
                <GridColumn title="Actions" cells={{ data: DeleteActionCell }} />
            </AppGrid>

            {isCreateDialogOpen && (
                <Dialog title="Create User" width={560} onClose={() => setIsCreateDialogOpen(false)}>
                    <div className="d-flex flex-column gap-3">
                        <Input
                            id="email"
                            name="email"
                            label="Email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={event => setEmail(String(event.value ?? ""))}
                        />

                        <Input
                            id="phoneNumber"
                            name="phone"
                            label="Phone Number"
                            type="tel"
                            autoComplete="tel"
                            value={phoneNumber}
                            onChange={event => setPhoneNumber(String(event.value ?? ""))}
                        />

                        <Input
                            id="password"
                            name="newPassword"
                            label="Password"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={event => setPassword(String(event.value ?? ""))}
                        />

                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={event => setConfirmPassword(String(event.value ?? ""))}
                        />
                        <MultiSelect label="Roles" data={roles} value={selectedRoles} onChange={event => setSelectedRoles(event.value)} />
                        <DropDownList label="Customer Assignment" data={customerModes} value={customerMode} onChange={event => setCustomerMode(event.value)} />
                        {customerInput}
                    </div>
                    <DialogActionsBar>
                        <Button type="button" onClick={() => setIsCreateDialogOpen(false)} disabled={isCreating}>Cancel</Button>
                        <Button type="button" themeColor="primary" disabled={!canCreate || isCreating} onClick={handleCreate}>Create</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default UserManagement;
